import { Loading } from '@brands/Components/LoadingSpinner/Loading';
import MinimalDateRangePicker from '@brands/Components/MinimalDateRangePicker/MinimalDateRangePicker';
import useAuditTrail from '@brands/hooks/useAuditTrail';
import useDebounce from '@brands/hooks/useDebounce';
import { UserRoleName } from '@brands/services/identity/types/UserProfile';
import { ColumnVisibilityKey } from '@brands/store/slices/columnVisibilitySlice';
import { TablePaginationKey } from '@brands/store/slices/tablePaginationSlice';
import { displayErrorDetails } from '@brands/Utils/displayError';
import { formatDateParts } from '@brands/Utils/formatDate';
import { triggerAuditTrailCsvDownload } from '@brands/Utils/triggerAuditTrailCsvDownload';
import Checkbox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import React, { useCallback, useMemo, useState } from 'react';
import { FiDownload } from 'react-icons/fi';

import { usePersistedRowsPerPage } from '../../../../hooks/usePersistedRowsPerPage';
import CADataGrid, { caGridStyles, ColumnDef } from '../../CareAdvisor/Components/CADataGrid';
import parentStyles from '../styles.module.scss';

const USER_ROLE_OPTIONS = [
  { value: UserRoleName.Patient, label: 'Patient' },
  { value: UserRoleName.Dependent, label: 'Dependent' },
  { value: UserRoleName.CareAdvisor, label: 'Care Advisor' },
  { value: UserRoleName.Provider, label: 'Provider' },
  { value: UserRoleName.Admin, label: 'Admin' },
  { value: UserRoleName.OrganizationAdmin, label: 'Org Admin' },
  { value: UserRoleName.SuperAdmin, label: 'Super Admin' },
];

const filterLabelSx = { fontSize: 12, fontWeight: 400 };

const SORT_KEY_MAP: Record<string, 'created_at' | 'user_name' | 'actor_name' | 'action' | 'user_id' | 'user_type'> = {
  user_name: 'user_name',
  user_id: 'user_id',
  action_message: 'action',
  actor: 'actor_name',
  created_at: 'created_at',
  user_type: 'user_type',
};

const AuditTrailList = (): JSX.Element => {
  const [filterValues, setFilterValues] = useState<{ [key: string]: string | string[] }>({});
  const [selectedRangeAuditTrailDate, setSelectedRangeAuditTrailDate] = useState<[Date, Date] | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = usePersistedRowsPerPage(
    TablePaginationKey.SuperAdminAuditTrail,
    10,
  );
  const [sortColKey, setSortColKey] = useState<string>('user_name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  const handleFilterChange = useCallback((key: string, value: string | string[]): void => {
    setFilterValues((prevFilters) => {
      const updatedFilters = { ...prevFilters };
      if ((typeof value === 'string' && value === '') || (Array.isArray(value) && value.length === 0)) {
        delete updatedFilters[key];
      } else {
        updatedFilters[key] = value;
      }
      return updatedFilters;
    });
    setPage(0);
  }, []);

  const handleSort = useCallback(
    (key: string) => {
      if (!SORT_KEY_MAP[key]) return;
      setPage(0);
      if (sortColKey === key) {
        setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
      } else {
        setSortColKey(key);
        setSortDirection('asc');
      }
    },
    [sortColKey],
  );

  const debouncedFilterValues = useDebounce(filterValues, 500);

  const params = useMemo(() => {
    return {
      sort_by: SORT_KEY_MAP[sortColKey] ?? 'created_at',
      sort_direction: sortDirection,
      limit: rowsPerPage,
      users_only: true,
      include_case_creation: true,
      ...debouncedFilterValues,
    };
  }, [debouncedFilterValues, sortColKey, sortDirection, rowsPerPage]);

  const {
    auditTrailList,
    loadNextPage,
    loadPreviousPage,
    dataLoading,
    loading,
    totalRecords,
  } = useAuditTrail(params, undefined, { paginationMode: 'page' });

  const formatDateToLocalString = (date: Date): string =>
    `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

  const handleDateRangeFilterChange = useCallback((key: string, value: [Date, Date] | null): void => {
    setFilterValues((prevFilters) => {
      const updatedFilters = { ...prevFilters };
      if (value) {
        if (value[0] === value[1] || (value[0] == null && value[1] == null)) {
          delete updatedFilters.date_from;
          delete updatedFilters.date_to;
        } else {
          updatedFilters.date_from = formatDateToLocalString(value[0]);
          updatedFilters.date_to = formatDateToLocalString(value[1]);
        }
      } else {
        delete updatedFilters.date_from;
        delete updatedFilters.date_to;
      }
      return updatedFilters;
    });
    if (key === 'audit_trail') {
      setSelectedRangeAuditTrailDate(value);
    }
    setPage(0);
  }, []);

  const handlePageChange = useCallback(
    async (nextPage: number): Promise<void> => {
      if (nextPage > page) {
        await loadNextPage();
      } else if (nextPage < page) {
        await loadPreviousPage();
      }
      setPage(nextPage);
    },
    [loadNextPage, loadPreviousPage, page],
  );

  const handleDownloadClick = async (): Promise<void> => {
    setIsLoading(true);
    try {
      await triggerAuditTrailCsvDownload(params);
    } catch (error: unknown) {
      displayErrorDetails(error);
    } finally {
      setIsLoading(false);
    }
  };

  const columns: ColumnDef<(typeof auditTrailList)[number]>[] = useMemo(
    () => [
      {
        key: 'user_name',
        label: 'Name',
        widthPx: 200,
        renderCell: (record) => {
          if (!record.user) return <span className={`fs-exclude ${caGridStyles.cellText}`}>N/A</span>;
          return (
            <span className="fs-exclude" style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.3 }}>
              <span className={caGridStyles.cellText}>{record.user.full_name}</span>
              {record.user.role?.name && (
                <span style={{ fontSize: '12px', color: 'rgba(13, 32, 53, 0.6)' }}>{record.user.role.name}</span>
              )}
            </span>
          );
        },
        filterContent: (
          <TextField
            variant="standard"
            fullWidth
            label="Name"
            value={filterValues.user_name || ''}
            onChange={(e) => handleFilterChange('user_name', e.target.value)}
            InputProps={{ disableUnderline: true, sx: { fontSize: 13, px: 0.5 } }}
            InputLabelProps={{ shrink: Boolean(filterValues.user_name), sx: filterLabelSx }}
          />
        ),
      },
      {
        key: 'user_id',
        label: 'User ID',
        widthPx: 100,
        renderCell: (record) => <span className={`fs-exclude ${caGridStyles.cellText}`}>{record.user?.id}</span>,
        filterContent: (
          <TextField
            variant="standard"
            fullWidth
            label="User ID"
            type="number"
            value={filterValues.user_id || ''}
            onChange={(e) => handleFilterChange('user_id', e.target.value)}
            InputProps={{ disableUnderline: true, sx: { fontSize: 13, px: 0.5 } }}
            InputLabelProps={{ shrink: Boolean(filterValues.user_id), sx: filterLabelSx }}
          />
        ),
      },
      {
        key: 'action_message',
        label: 'Action',
        widthPx: 300,
        renderCell: (record) => <span className={`fs-exclude ${caGridStyles.cellText}`}>{record.action_message}</span>,
        filterContent: (
          <TextField
            variant="standard"
            fullWidth
            label="Action"
            value={filterValues.action_message || ''}
            onChange={(e) => handleFilterChange('action_message', e.target.value)}
            InputProps={{ disableUnderline: true, sx: { fontSize: 13, px: 0.5 } }}
            InputLabelProps={{ shrink: Boolean(filterValues.action_message), sx: filterLabelSx }}
          />
        ),
      },
      {
        key: 'actor',
        label: 'Modified by',
        widthPx: 200,
        renderCell: (record) => {
          if (!record.actor) return <span className={`fs-exclude ${caGridStyles.cellText}`}>N/A</span>;
          return (
            <span className="fs-exclude" style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.3 }}>
              <span className={caGridStyles.cellText}>{record.actor.full_name}</span>
              {record.actor.role?.name && (
                <span style={{ fontSize: '12px', color: 'rgba(13, 32, 53, 0.6)' }}>{record.actor.role.name}</span>
              )}
            </span>
          );
        },
        filterContent: (
          <TextField
            variant="standard"
            fullWidth
            label="Modified by"
            value={filterValues.actor_name || ''}
            onChange={(e) => handleFilterChange('actor_name', e.target.value)}
            InputProps={{ disableUnderline: true, sx: { fontSize: 13, px: 0.5 } }}
            InputLabelProps={{ shrink: Boolean(filterValues.actor_name), sx: filterLabelSx }}
          />
        ),
      },
      {
        key: 'created_at',
        label: 'Modification date',
        widthPx: 180,
        renderCell: (record) => {
          if (!record.created_at) return <span className={`fs-unmask ${caGridStyles.cellText}`}>N/A</span>;
          const parts = formatDateParts(record.created_at);
          return (
            <span
              className={`fs-unmask ${caGridStyles.cellText}`}
              style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.3 }}
            >
              <span>{parts.date}</span>
              <span style={{ color: 'rgba(13, 32, 53, 0.6)' }}>{parts.time}</span>
            </span>
          );
        },
        filterContent: (
          <MinimalDateRangePicker
            handleDateRangeFilterChange={handleDateRangeFilterChange}
            filterKey="audit_trail"
            selectedRange={selectedRangeAuditTrailDate}
            width="100%"
            openToLeft
          />
        ),
      },
      {
        key: 'user_type',
        label: 'User type',
        widthPx: 160,
        renderCell: (record) => (
          <span className={`fs-unmask ${caGridStyles.cellText}`}>{record.user?.role?.name || '-'}</span>
        ),
        filterContent: (
          <FormControl variant="standard" fullWidth>
            <InputLabel
              shrink={(Array.isArray(filterValues.user_role) ? (filterValues.user_role as string[]) : []).length > 0}
              sx={filterLabelSx}
            >
              User type
            </InputLabel>
            <Select
              multiple
              value={Array.isArray(filterValues.user_role) ? (filterValues.user_role as string[]) : []}
              onChange={(e) => handleFilterChange('user_role', e.target.value as string[])}
              disableUnderline
              sx={{ fontSize: 13, px: 0.5, '& .MuiSelect-select': { paddingRight: '28px' } }}
              renderValue={(selected) =>
                (selected as string[])
                  .map((v) => USER_ROLE_OPTIONS.find((r) => r.value === v)?.label ?? v)
                  .join(', ')
              }
            >
              {USER_ROLE_OPTIONS.map((role) => (
                <MenuItem key={role.value} value={role.value}>
                  <Checkbox
                    size="small"
                    checked={(Array.isArray(filterValues.user_role) ? (filterValues.user_role as string[]) : []).includes(role.value)}
                  />
                  <ListItemText primary={role.label} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ),
      },
    ],
    [filterValues, handleFilterChange, handleDateRangeFilterChange, selectedRangeAuditTrailDate],
  );

  return (
    <div className={parentStyles.casesPageContainer}>
      <div className={parentStyles.casesPageHeader}>
        {/* Page heading — h1 in the design, matching the admin Cases heading. */}
        <h1 className={parentStyles.sectionTitle}>Audit trail</h1>
        <button type="button" className={parentStyles.createCaseBtn} onClick={handleDownloadClick} disabled={isLoading}>
          <FiDownload size={16} />
          Download CSV
        </button>
      </div>

      <div className={parentStyles.casesGridWrapper}>
        {dataLoading && auditTrailList.length === 0 ? (
          <Loading dotted />
        ) : (
          <CADataGrid
            columns={columns}
            rows={auditTrailList}
            getRowId={(row) => row.id}
            count={totalRecords}
            page={page}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[10, 25, 100]}
            onPageChange={(p) => {
              void handlePageChange(p);
            }}
            onRowsPerPageChange={(rpp) => {
              setRowsPerPage(rpp);
              setPage(0);
            }}
            loading={dataLoading || loading}
            sortBy={sortColKey}
            sortDirection={sortDirection}
            onSort={handleSort}
            columnVisibilityKey={ColumnVisibilityKey.SuperAdminAuditTrail}
          />
        )}
      </div>
    </div>
  );
};

export default AuditTrailList;
